import { useState, useEffect, useMemo } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { textFieldValidation } from '@/utils/validations';
import { Statuses } from '@/constant';
import usePagination from '@/hooks/usePagination';
import { ErrorApiResponse, PaginatedApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  BoatListQueryParams,
  BoatPayload,
  BoatResponse,
} from '@/types/boat-management.types';
import {
  useCreateBoatMutation,
  useDeleteBoatMutation,
  useGetBoatByIdQuery,
  useGetBoatsQuery,
  useUpdateBoatMutation,
} from '@/services/BoatManagementService';
import { BoatModal } from './BoatModal';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

// Validation schema for create/edit boat (kept for reference, used in BoatModal)
const boatValidationSchema = Yup.object().shape({
  name: textFieldValidation('Boat Name', 2, 50, true),
  type: Yup.string().required('Boat type is required'),
  length: Yup.string()
    .required('Length is required')
    .matches(
      /^\d+(?:[.,]\d{1,2})?$/,
      "Length must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
    ),
  width: Yup.string()
    .required('Width is required')
    .matches(
      /^\d+(?:[.,]\d{1,2})?$/,
      "Width must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
    ),
  draft: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Draft must be a valid number')
    .nullable(),
  height: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Height must be a valid number')
    .nullable(),
  weight: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Weight must be a valid number')
    .nullable(),
  registrationNumber: textFieldValidation('Registration Number', 3, 20, true),
  boaterId: Yup.string().required('Boater is required'),
  unit: Yup.string().oneOf(['m', 'ft']).required('Unit is required'),
});

interface IBoatData {
  id?: string;
  name?: string;
  model?: string;
  type?: string;
  length?: string;
  width?: string;
  draft?: string;
  height?: string;
  weight?: string;
  isCustomBoatModel?: boolean;
  registrationNumber?: string;
  boater?: {
    id?: string;
    name: string;
    email?: string;
  };
  createdAt?: string;
  status?: string;
  unit?: 'm' | 'ft';
}

export const normalizeNumericFieldValue = (value: string) =>
  value.trim().replace(/\s/g, '').replace(/,/g, '.');

const BoatListings = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    boat: IBoatData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, boat: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    boatId: string | null;
  }>({ isOpen: false, boatId: null });
  const [formData, setFormData] = useState<Partial<IBoatData>>({
    name: '',
    type: 'Sailboat',
    model: '',
    length: '',
    width: '',
    draft: '',
    height: '',
    weight: '',
    registrationNumber: '',
    boater: { id: '', name: '', email: '' },
    status: 'Active',
    isCustomBoatModel: false,
    unit: 'ft',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [createBoat, { isLoading: isCreating }] = useCreateBoatMutation();
  const [updateBoat, { isLoading: isUpdating }] = useUpdateBoatMutation();
  const [deleteBoat] = useDeleteBoatMutation();
  const [selectedBoatId, setSelectedBoatId] = useState<string | null>(null);
  const { hasPermission } = usePermissions();

  // Fetch boats from API
  const queryParams: BoatListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    type: typeFilter || undefined,
  };

  const {
    data: boatsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetBoatsQuery(queryParams) as {
    data: PaginatedApiResponse<BoatResponse[]> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  const { data: selectedBoat, isSuccess: isBoatLoaded } = useGetBoatByIdQuery(
    selectedBoatId!,
    {
      skip: !selectedBoatId,
    }
  );

  useEffect(() => {
    if (boatsResponse) {
      handleTotalPages(
        boatsResponse.total_pages ?? boatsResponse.totalPages ?? 1
      );
      handleTotalItems(
        boatsResponse.total_items ?? boatsResponse.totalItems ?? 0
      );
    }
  }, [boatsResponse, handleTotalPages, handleTotalItems]);

  const filteredData: IBoatData[] = useMemo(() => {
    if (!boatsResponse?.data) return [];

    return boatsResponse.data.map(
      (boat: BoatResponse): IBoatData => ({
        id: boat.id,
        name: boat.name,
        model: boat.boat_model_name,
        type: boat.type,
        length: boat.length,
        width: boat.width,
        draft: boat.draft,
        height: boat.height,
        weight: boat.weight,
        unit: boat.unit || 'ft',
        registrationNumber: boat.registration_number,
        boater: {
          id: (boat.boater?.id as string) ?? '-',
          name: (boat.boater?.name as string) ?? (boat.boater?.email as string),
          email: (boat.boater?.email as string) ?? '-',
        },
        createdAt: boat.created_at,
        status: boat.status === String(Statuses.ACTIVE) ? 'Active' : 'Inactive',
        isCustomBoatModel: boat.is_custom_boat_model,
      })
    );
  }, [boatsResponse]);

  const resetFilters = () => {
    setTypeFilter('');
    if (searchTerm) resetSearch();
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  useEffect(() => {
    if (selectedBoat && isBoatLoaded && modalState.mode !== 'create') {
      setFormData({
        id: selectedBoat.id,
        name: selectedBoat.name,
        model: selectedBoat.boat_model_name,
        type: selectedBoat.type,
        length: selectedBoat.length,
        width: selectedBoat.width,
        draft: selectedBoat.draft,
        height: selectedBoat.height,
        weight: selectedBoat.weight,
        unit: selectedBoat.unit || 'ft',
        registrationNumber: selectedBoat.registration_number,
        boater: {
          id: selectedBoat.boater_id,
          name: selectedBoat.boater_name ?? selectedBoat.boater?.email,
          email: selectedBoat.boater?.email,
        },
        isCustomBoatModel: selectedBoat.is_custom_boat_model ?? false,
        createdAt: selectedBoat.created_at,
        status:
          selectedBoat.status === String(Statuses.ACTIVE)
            ? 'Active'
            : 'Inactive',
      });
    }
  }, [selectedBoat, isBoatLoaded, modalState.mode]);

  const handleOpenModal = (
    boat: IBoatData | null,
    mode: 'view' | 'edit' | 'create'
  ) => {
    setSelectedBoatId(boat?.id || null);
    setModalState({ isOpen: true, boat, mode });
    if (mode === 'create') {
      setFormData({
        name: '',
        type: 'Sailboat',
        model: '',
        length: '',
        width: '',
        draft: '',
        height: '',
        weight: '',
        registrationNumber: '',
        boater: { id: '', name: '', email: '' },
        status: 'Active',
        isCustomBoatModel: false,
        unit: 'ft',
      });
      setErrors({});
    }
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, boat: null, mode: 'view' });
    setFormData({});
    setErrors({});
    setSelectedBoatId(null);
  };

  const handleSubmit = async (isCustomModel: boolean, boatModelId?: string) => {
    try {
      await boatValidationSchema.validate(
        {
          name: formData.name,
          type: formData.type,
          length: formData.length,
          width: formData.width,
          draft: formData.draft,
          height: formData.height,
          weight: formData.weight,
          registrationNumber: formData.registrationNumber,
          boaterId: formData.boater?.id,
          unit: formData.unit,
        },
        { abortEarly: false }
      );

      const payload: BoatPayload = {
        name: formData.name || '',
        type: formData.type || '',
        length: normalizeNumericFieldValue(formData.length || ''),
        width: normalizeNumericFieldValue(formData.width || ''),
        draft: normalizeNumericFieldValue(formData.draft || ''),
        height: normalizeNumericFieldValue(formData.height || ''),
        weight: normalizeNumericFieldValue(formData.weight || ''),
        registration_number: formData.registrationNumber || '',
        boater_id: formData.boater?.id || '',
        status: Statuses.ACTIVE,
        is_custom_boat_model: isCustomModel,
        unit: formData.unit || 'ft',
        boat_model_name: formData.model || '',
        ...(boatModelId && !isCustomModel && { boat_model_id: boatModelId }),
      };

      if (modalState.mode === 'create') {
        await createBoat(payload).unwrap();
        enqueueSnackbar('Boat created successfully', { variant: 'success' });
      } else if (modalState.mode === 'edit' && modalState.boat) {
        await updateBoat({
          boat_id: modalState.boat.id as string,
          data: payload,
        }).unwrap();
        enqueueSnackbar('Boat updated successfully', { variant: 'success' });
      }

      handleCloseModal();
      refetch();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      } else {
        const errorMessage = err
          ? (err as ErrorApiResponse)?.data?.message
          : 'An error occurred';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.boatId) return;
    try {
      await deleteBoat(confirmDeleteState.boatId).unwrap();
      enqueueSnackbar('Boat deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, boatId: null });
      refetch();
    } catch (err) {
      const errorMessage = err
        ? (err as ErrorApiResponse)?.data?.message
        : 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteState({ isOpen: false, boatId: null });
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IBoatData) => row.name,
        id: 'name',
        header: 'Boat Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBoatData } }) =>
          hasPermission('boat_management_read') ? (
            <button
              onClick={() => handleOpenModal(row.original, 'view')}
              className="text-sm font-medium text-gray-900 hover:text-primary-active"
              aria-label={`View ${row.original.name}`}
            >
              {row.original.name}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {row.original.name}
            </span>
          ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IBoatData) => row.type,
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBoatData } }) => (
          <span className="text-gray-800 font-normal">{row.original.type}</span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IBoatData) => row?.boater?.name,
        id: 'boater',
        header: 'Boater',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBoatData } }) => (
          <span className="text-gray-800 font-normal">
            {row?.original.boater?.name}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IBoatData) => row.createdAt,
        id: 'createdAt',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBoatData } }) => (
          <span className="text-gray-700 contrast:text-gray-600 font-normal">
            {formatDisplayDate(row.original.createdAt)}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBoatData } }) => (
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label={`Actions for ${row.original.name}`}
          >
            {hasPermission('boat_management_read') && (
              <button
                type="button"
                onClick={() => handleOpenModal(row.original, 'view')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View boat details"
                aria-label={`View ${row.original.name} details`}
              >
                <KeenIcon
                  type="filled"
                  icon="eye"
                  className="text-gray-500"
                  aria-hidden="true"
                />
              </button>
            )}
            {hasPermission('boat_management_update') && (
              <button
                type="button"
                onClick={() => handleOpenModal(row.original, 'edit')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Edit boat"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                  aria-hidden="true"
                />
              </button>
            )}
            {hasPermission('boat_management_delete') && (
              <button
                type="button"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    boatId: row.original.id as string,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Delete boat"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-red-500"
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [hasPermission]
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0" role="search">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-boats"
              aria-label="Search boats"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-boats"
                placeholder="Search boats"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search boats by name or registration number"
                role="searchbox"
              />
            </label>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          role="toolbar"
          aria-label="Filter controls"
        >
          {(typeFilter || searchTerm) && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <span className="sr-only">Loading boats data</span>
            Loading boats...
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-500"
            role="alert"
            aria-live="assertive"
          >
            Failed to load boats. Please try again.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isLoading || isFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'name', desc: false }]}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={!!typeFilter || !!searchTerm}
            aria-label="Boat listings table"
          />
        )}
        <BoatModal
          open={modalState.isOpen}
          onClose={handleCloseModal}
          mode={modalState.mode}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          onSubmit={handleSubmit}
          isSubmitting={isCreating || isUpdating}
        />
        <ConfirmationDialog
          open={confirmDeleteState.isOpen}
          onClose={() => setConfirmDeleteState({ isOpen: false, boatId: null })}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this boat?"
          cancelButtonText="No"
          confirmButtonText="Yes"
        />
      </div>
    </div>
  );
};

export { BoatListings };
